
import { supabase } from '@/integrations/supabase/client';
import { healthySupabaseCall } from '../supabaseHealth';

export type NotificationType = 'like' | 'comment' | 'follow' | 'achievement' | 'friend_request' | 'game_invite' | 'badge_earned' | 'round_completed' | 'social_activity' | 'coaching_booked' | 'coaching_cancelled' | 'direct_message' | 'payment_reminder';

export interface Notification {
  id: string;
  type: NotificationType;
  title?: string | null;
  message: string | null;
  data?: Record<string, any> | null;
  read: boolean;
  created_at: string;
  related_user_id: string | null;
  related_post_id: string | null;
  user?: {
    id: string;
    display_name: string;
    nickname?: string;
    avatar_url: string | null;
  };
}

export const getNotifications = async (limit: number = 50, offset: number = 0) => {
  return await healthySupabaseCall(async () => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          id,
          type,
          title,
          message,
          data,
          read,
          created_at,
          related_user_id,
          related_post_id,
          profiles:related_user_id(
            id,
            display_name,
            nickname,
            avatar_url
          )
        `)
        .eq('user_id', user.id)
        .order('read', { ascending: true }) // Unread first
        .order('created_at', { ascending: false }) // Then by date
        .range(offset, offset + limit - 1);

      if (error) {
        // If table doesn't exist, return empty array instead of throwing
        if (error.code === '42P01' || error.message.includes('relation "notifications" does not exist')) {
          console.warn('Notifications table does not exist yet. Returning empty notifications array.');
          return [];
        }
        throw error;
      }

      // Map notifications and fetch missing inviter profiles for game_invite notifications
      const mappedNotifications = (data || []).map(notification => ({
        ...notification,
        type: notification.type as NotificationType,
        user: notification.profiles ? {
          id: notification.profiles.id,
          display_name: notification.profiles.display_name,
          nickname: notification.profiles.nickname,
          avatar_url: notification.profiles.avatar_url
        } : undefined
      }));

      // For game_invite notifications without user profile, fetch from data.inviter_id
      const enrichedNotifications = await Promise.all(
        mappedNotifications.map(async (notification) => {
          if (notification.type === 'game_invite' && !notification.user && notification.data?.inviter_id) {
            try {
              const { data: inviterProfile } = await supabase
                .from('profiles')
                .select('id, display_name, nickname, avatar_url')
                .eq('id', notification.data.inviter_id)
                .single();

              if (inviterProfile) {
                return {
                  ...notification,
                  user: {
                    id: inviterProfile.id,
                    display_name: inviterProfile.display_name || 'Someone',
                    nickname: inviterProfile.nickname,
                    avatar_url: inviterProfile.avatar_url
                  }
                };
              }
            } catch (error) {
              console.error('Failed to fetch inviter profile for notification:', notification.id, error);
            }
          }
          return notification;
        })
      );

      return enrichedNotifications;
    } catch (error: any) {
      // Graceful fallback for missing table
      if (error.code === '42P01' || error.message?.includes('relation "notifications" does not exist')) {
        console.warn('Notifications table does not exist yet. Returning empty notifications array.');
        return [];
      }
      throw error;
    }
  });
};

export const markNotificationAsRead = async (notificationId: string) => {
  return await healthySupabaseCall(async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) {
        // If table doesn't exist, silently return
        if (error.code === '42P01' || error.message.includes('relation "notifications" does not exist')) {
          console.warn('Notifications table does not exist yet. Skipping mark as read operation.');
          return;
        }
        throw error;
      }
    } catch (error: any) {
      // Graceful fallback for missing table
      if (error.code === '42P01' || error.message?.includes('relation "notifications" does not exist')) {
        console.warn('Notifications table does not exist yet. Skipping mark as read operation.');
        return;
      }
      throw error;
    }
  });
};

export const markAllNotificationsAsRead = async () => {
  return await healthySupabaseCall(async () => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) {
        // If table doesn't exist, silently return
        if (error.code === '42P01' || error.message.includes('relation "notifications" does not exist')) {
          console.warn('Notifications table does not exist yet. Skipping mark all as read operation.');
          return;
        }
        throw error;
      }
    } catch (error: any) {
      // Graceful fallback for missing table
      if (error.code === '42P01' || error.message?.includes('relation "notifications" does not exist')) {
        console.warn('Notifications table does not exist yet. Skipping mark all as read operation.');
        return;
      }
      throw error;
    }
  });
};

// Notification types that are displayed in the UI tabs
// Activity tab: social, achievement, round, tournament/club types
// My friends tab: friend_request, friend_accepted, follow, friend_posted
const DISPLAYED_NOTIFICATION_TYPES = [
  // Social
  'post_liked', 'post_commented', 'post_shared',
  'follow', 'friend_posted', 'friend_request', 'friend_accepted', 'game_invite',
  // Achievement & rounds
  'badge_earned', 'achievement', 'round_completed', 'friend_round_completed',
  // Tournament & club
  'tournament_update', 'round_activated', 'match_scheduled', 'results_published',
  'bracket_updated', 'group_assigned', 'match_result', 'championship_complete',
  'qualified_for_knockout', 'admin_announcement', 'channel_created', 'channel_deleted',
  // Coaching
  'coaching_booked', 'coaching_cancelled',
  // Payments & messages
  'payment_reminder', 'direct_message',
];

export const getUnreadNotificationCount = async () => {
  return await healthySupabaseCall(async () => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      return 0;
    }

    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .eq('read', false)
        .in('type', DISPLAYED_NOTIFICATION_TYPES);

      if (error) {
        // If table doesn't exist, return 0
        if (error.code === '42P01' || error.message.includes('relation "notifications" does not exist')) {
          console.warn('Notifications table does not exist yet. Returning 0 unread count.');
          return 0;
        }
        throw error;
      }
      return count || 0;
    } catch (error: any) {
      // Graceful fallback for missing table
      if (error.code === '42P01' || error.message?.includes('relation "notifications" does not exist')) {
        console.warn('Notifications table does not exist yet. Returning 0 unread count.');
        return 0;
      }
      throw error;
    }
  });
};

export const deleteNotification = async (notificationId: string) => {
  return await healthySupabaseCall(async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        // If table doesn't exist, silently return
        if (error.code === '42P01' || error.message.includes('relation "notifications" does not exist')) {
          console.warn('Notifications table does not exist yet. Skipping delete operation.');
          return;
        }
        throw error;
      }
    } catch (error: any) {
      // Graceful fallback for missing table
      if (error.code === '42P01' || error.message?.includes('relation "notifications" does not exist')) {
        console.warn('Notifications table does not exist yet. Skipping delete operation.');
        return;
      }
      throw error;
    }
  });
};

export const deleteAllNotifications = async () => {
  return await healthySupabaseCall(async () => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);

      if (error) {
        if (error.code === '42P01' || error.message.includes('relation "notifications" does not exist')) {
          console.warn('Notifications table does not exist yet.');
          return;
        }
        throw error;
      }
    } catch (error: any) {
      if (error.code === '42P01' || error.message?.includes('relation "notifications" does not exist')) {
        return;
      }
      throw error;
    }
  });
};

export const createNotification = async (
  userId: string,
  type: NotificationType,
  message: string,
  relatedUserId?: string,
  relatedPostId?: string
) => {
  return await healthySupabaseCall(async () => {
    try {
      const { data: result, error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type,
          message,
          related_user_id: relatedUserId || null,
          related_post_id: relatedPostId || null
        })
        .select()
        .single();

      if (error) {
        // If table doesn't exist, silently return null
        if (error.code === '42P01' || error.message.includes('relation "notifications" does not exist')) {
          console.warn('Notifications table does not exist yet. Skipping notification creation.');
          return null;
        }
        throw error;
      }
      return result;
    } catch (error: any) {
      // Graceful fallback for missing table
      if (error.code === '42P01' || error.message?.includes('relation "notifications" does not exist')) {
        console.warn('Notifications table does not exist yet. Skipping notification creation.');
        return null;
      }
      throw error;
    }
  });
};
